import { apiFetch } from './client';
import { DashboardTile, Report } from '../../types/dashboard';

export interface DashboardLayoutResponse {
  data: DashboardPosition[];
  message?: string;
}

export interface DashboardPosition {
  id: string;
  x: number;
  y: number;
  w?: number;  // Width in grid units
  h?: number;  // Height in grid units
  isExpanded?: boolean;
}

// Fallback to localStorage if API fails
const STORAGE_KEY = 'nova-dashboard-layout';

// Default tiles configuration - 3 standardized chart sizes for responsive design
// Small (4 cols): 3 charts per row, Medium (6 cols): 2 charts per row, Large (12 cols): 1 chart per row
const getDefaultTiles = (): DashboardTile[] => [
  // Analytics Section - Monthly Listeners and Followers side by side (50% each)
  { id: 'monthly-listeners', x: 0, y: 0, w: 6, h: 8, title: 'Revenue & Streaming Performance', type: 'chart', isExpanded: false, minW: 6, minH: 6, maxW: 12, maxH: 25 },
  { id: 'monthly-followers', x: 0, y: 0, w: 12, h: 8, title: 'Monthly Followers', type: 'chart', isExpanded: false, minW: 6, minH: 6, maxW: 12, maxH: 25 },
  
  // Small charts (4 cols) - row 2: Brand Affinity, Editorial Playlists (YouTube Views removed)
  { id: 'brand-affinity', x: 0, y: 8, w: 6, h: 4, title: 'Brand Affinity', type: 'chart', isExpanded: false, minW: 3, minH: 3, maxW: 6, maxH: 6 },
  { id: 'editorial-playlists', x: 6, y: 8, w: 6, h: 4, title: 'Editorial Playlist Reach', type: 'chart', isExpanded: false, minW: 3, minH: 3, maxW: 6, maxH: 6 },
  
  // Medium charts (6 cols) - 2 per row
    { id: 'geography', x: 0, y: 12, w: 6, h: 5, title: 'Geographic Distribution', type: 'chart', isExpanded: false, minW: 6, minH: 4, maxW: 12, maxH: 8 },
  { id: 'platforms', x: 6, y: 12, w: 6, h: 5, title: 'Total Playlist Count', type: 'chart', isExpanded: false, minW: 6, minH: 4, maxW: 12, maxH: 8 },
  
  // Growth charts (4 cols each) - row 4: Weekly Growth, Monthly Growth, Platform Growth Comparison
  { id: 'weekly-growth', x: 0, y: 17, w: 4, h: 4, title: 'Weekly Growth', type: 'chart', isExpanded: false, minW: 3, minH: 3, maxW: 6, maxH: 6 },
  { id: 'monthly-growth', x: 4, y: 17, w: 4, h: 4, title: 'Monthly Growth', type: 'chart', isExpanded: false, minW: 3, minH: 3, maxW: 6, maxH: 6 },
  { id: 'platform-growth-comparison', x: 8, y: 17, w: 4, h: 4, title: 'Platform Growth Comparison', type: 'chart', isExpanded: false, minW: 3, minH: 3, maxW: 6, maxH: 6 },
  
  // Instagram distributions
  { id: 'instagram-gender-distribution', x: 0, y: 21, w: 6, h: 5, title: 'Instagram Gender Distribution', type: 'chart', isExpanded: false, minW: 4, minH: 4, maxW: 12, maxH: 10 },
  { id: 'instagram-age-distribution', x: 6, y: 21, w: 6, h: 5, title: 'Instagram Age Distribution', type: 'chart', isExpanded: false, minW: 4, minH: 4, maxW: 12, maxH: 10 },
  { id: 'youtube-gender-distribution', x: 0, y: 26, w: 6, h: 5, title: 'YouTube Gender Distribution', type: 'chart', isExpanded: false, minW: 4, minH: 4, maxW: 12, maxH: 10 },
  
  // Audience Reach Pyramid - hidden for now
  // { id: 'downloads', x: 8, y: 21, w: 4, h: 4, title: 'Audience Reach Pyramid', type: 'metric', isExpanded: false, minW: 4, minH: 3, maxW: 6, maxH: 6 },
  
  // Spotify Funnel Chart - positioned at the end
  { id: 'spotify-funnel', x: 0, y: 21, w: 6, h: 6, title: 'Spotify Engagement Funnel', type: 'chart', isExpanded: false, minW: 6, minH: 5, maxW: 12, maxH: 10 },
  { id: 'engagement-rates', x: 6, y: 21, w: 6, h: 6, title: 'Engagement Rates', type: 'chart', isExpanded: false, minW: 6, minH: 4, maxW: 12, maxH: 10 },
  
];

// Platform mapping interface for secondary DB charts
export interface PlatformMapping {
  artistId: number;
  platformArtistId: string;
  platformId: number;
  dateRecorded: string;
  dateUpdated: string;
  source: string;
}

export const dashboardService = {
  /**
   * Get platform mapping for secondary database charts
   * @param artistId - Artist ID to get mapping for
   * @returns Promise<PlatformMapping | null>
   */
  async getPlatformMapping(artistId: string): Promise<PlatformMapping | null> {
    try {
      const response = await apiFetch(`/analytics/artist/platform-mapping/${artistId}`);
      return response as PlatformMapping;
    } catch (_error: any) {
      return null;
    }
  },

  /**
   * Get user's saved dashboard layout - backend returns full tile data with w/h
   * @returns Promise<DashboardTile[]>
   */
  async getUserLayout(): Promise<DashboardTile[]> {
    // Clear any old localStorage cache that might have w/h values
    localStorage.removeItem(STORAGE_KEY);

    try {
      const response: any = await apiFetch('/dashboard/layout');

      if (response.data && response.data.length > 0) {
        // Backend now returns full tiles with w, h from dashboard_reports table
        const tiles: DashboardTile[] = response.data.map((tile: any) => ({
          id: tile.id,
          x: tile.x,
          y: tile.y,
          w: tile.w,
          h: tile.h,
          title: tile.title || dashboardService.getTileTitle(tile.id),
          type: dashboardService.getTileType(tile.id) as 'chart' | 'metric' | 'map' | 'list' | 'table',
          isExpanded: tile.isExpanded || false,
          minW: tile.minW || (tile.w === 4 ? 3 : tile.w === 6 ? 6 : 1),
          minH: tile.minH || (tile.h >= 6 ? 5 : 3),
          maxW: tile.maxW || 12,
          maxH: tile.maxH || (tile.h >= 6 ? 25 : 8)
        }));

        return tiles;
      }

      // Fallback to defaults if no data
      return getDefaultTiles();
    } catch (_error) {
      return getDefaultTiles();
    }
  },

  /**
   * Save user's dashboard layout - only positions, not dimensions
   * @param tiles - Array of dashboard tiles to save
   * @returns Promise<boolean>
   */
  async saveUserLayout(tiles: DashboardTile[]): Promise<boolean> {
    try {
      // Send positions AND dimensions (w, h) to backend
      const positions: DashboardPosition[] = tiles.map(tile => ({
        id: tile.id,
        x: tile.x,
        y: tile.y,
        w: tile.w,  // Include width
        h: tile.h,  // Include height
        isExpanded: tile.isExpanded || false
      }));
      
      const response: DashboardLayoutResponse = await apiFetch('/dashboard/layout', {
        method: 'PUT',
        body: JSON.stringify({ positions }),
      });
      return true;
    } catch (_error) {
      return false;
    }
  },

  /**
   * Reset user's dashboard layout to default
   * @returns Promise<DashboardTile[]>
   */
  async resetUserLayout(): Promise<DashboardTile[]> {
    const defaultTiles = getDefaultTiles();
    
    try {
      await apiFetch('/dashboard/reset', {
        method: 'POST',
      });
      
      // Always return default tiles since dimensions are fixed
      return defaultTiles;
    } catch (_error) {
      return defaultTiles;
    }
  },

  /**
   * Get tile title by ID
   */
  getTileTitle(id: string): string {
    const titles: Record<string, string> = {
      downloads: 'Audience Reach Pyramid',
      geography: 'Geographic Distribution',
      platforms: 'Total Playlist Count',
      'monthly-listeners': 'Revenue & Streaming Performance',
      'monthly-followers': 'Monthly Followers',
      'youtube-views': 'YouTube Music Views',
      'editorial-playlists': 'Editorial Playlist Reach',
      'brand-affinity': 'Brand Affinity',
      'weekly-growth': 'Weekly Growth',
      'monthly-growth': 'Monthly Growth',
      'platform-growth-comparison': 'Platform Growth Comparison',
      'spotify-funnel': 'Spotify Engagement Funnel',
      'engagement-rates': 'Engagement Rates',
      'instagram-gender-distribution': 'Instagram Gender Distribution',
      'instagram-age-distribution': 'Instagram Age Distribution',
      'youtube-gender-distribution': 'YouTube Gender Distribution'
    };
    return titles[id] || 'Dashboard Tile';
  },

  /**
   * Get tile type by ID
   */
  getTileType(id: string): string {
    const types: Record<string, string> = {
      streams: 'metric',
      downloads: 'metric',
      geography: 'chart',
      platforms: 'chart',
      trending: 'chart',
      'monthly-listeners': 'chart',
      'monthly-followers': 'chart',
      'youtube-views': 'metric',
      'editorial-playlists': 'chart',
      'brand-affinity': 'chart',
      'weekly-growth': 'chart',
      'monthly-growth': 'chart',
      'platform-growth-comparison': 'chart',
      'spotify-funnel': 'chart',
      'engagement-rates': 'chart',
      'instagram-gender-distribution': 'chart',
      'instagram-age-distribution': 'chart',
      'youtube-gender-distribution': 'chart'
    };
    return types[id] || 'chart';
  },

  /**
   * Get all available reports with user's visibility status
   * @returns Promise<Report[]>
   */
  async getAvailableReports(): Promise<Report[]> {
    try {
      const response = await apiFetch('/dashboard/available-reports');
      return response.reports;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Toggle tile visibility for current user
   * @param tileId - ID of the tile to toggle
   * @param isVisible - New visibility state
   * @returns Promise<boolean>
   */
  async toggleTileVisibility(tileId: string, isVisible: boolean): Promise<boolean> {
    try {
      const response = await apiFetch('/dashboard/toggle-tile', {
        method: 'POST',
        body: JSON.stringify({ tileId, isVisible }),
      });

      return response.success;
    } catch (error: any) {
      throw error;
    }
  }
};
